/**
 * REST client for scheduled jobs (`/jobs/schedules*` on the async HTTP API)
 * — Vera U9/U13. Backs the stop-schedule deep-link page and the console's
 * Jobs panel. Built on the shared `apiClient` plumbing; the one bespoke
 * piece is the typed 409 `ActiveScheduleLimit` payload the enable path
 * surfaces so the UI can render the deactivate-one chooser (FR-SCHED-4).
 */

import { authHeader, requestJson, safeText } from './apiClient.ts';
import { asyncApiConfig } from '../config/async-api-config.ts';
import { requestTimeout } from '../config/requestTimeout.ts';

export type JobSchedule =
  | { readonly type: 'one-time'; readonly at: string }
  | { readonly type: 'recurring'; readonly cron: string };

export interface JobDefinition {
  readonly jobId: string;
  readonly workflowId: string;
  readonly schedule: JobSchedule;
  readonly params?: Readonly<Record<string, string>>;
  readonly timeZone?: string;
  readonly enabled: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** The enable path's typed 409 — carries the active list for the chooser. */
export class ActiveScheduleLimitError extends Error {
  readonly limit: number;
  readonly active: readonly { jobId: string; workflowId: string; schedule: JobSchedule }[];
  constructor(limit: number, message: string, active: ActiveScheduleLimitError['active']) {
    super(message);
    this.name = 'ActiveScheduleLimitError';
    this.limit = limit;
    this.active = active;
  }
}

function schedulesBase(): string {
  return `${asyncApiConfig.baseUrl}/jobs/schedules`;
}

export async function listSchedules(): Promise<readonly JobDefinition[]> {
  const body = await requestJson<{ items?: readonly JobDefinition[] }>(schedulesBase(), {
    logTag: 'jobsApi',
    friendlyError: 'Could not load your schedules.',
  });
  return body.items ?? [];
}

export async function getSchedule(jobId: string): Promise<JobDefinition> {
  return requestJson<JobDefinition>(`${schedulesBase()}/${encodeURIComponent(jobId)}`, {
    logTag: 'jobsApi',
    friendlyError: 'Could not load that schedule.',
  });
}

export async function disableSchedule(jobId: string): Promise<JobDefinition> {
  return requestJson<JobDefinition>(`${schedulesBase()}/${encodeURIComponent(jobId)}/disable`, {
    method: 'POST',
    logTag: 'jobsApi',
    friendlyError: 'Could not stop that schedule.',
  });
}

/**
 * Enable — bespoke fetch (not `requestJson`) because a 409 here is not an
 * error to swallow: it is the cap chooser's data (FR-SCHED-4).
 */
export async function enableSchedule(jobId: string): Promise<JobDefinition> {
  const response = await fetch(`${schedulesBase()}/${encodeURIComponent(jobId)}/enable`, {
    method: 'POST',
    headers: await authHeader(),
    signal: requestTimeout(),
  });
  if (response.status === 409) {
    const body = (await response.json()) as {
      limit?: number;
      message?: string;
      active?: { jobId: string; workflowId: string; schedule: JobSchedule }[];
    };
    throw new ActiveScheduleLimitError(
      body.limit ?? 2,
      body.message ?? 'You are at your active-schedule limit.',
      body.active ?? [],
    );
  }
  if (!response.ok) {
    console.error(`[jobsApi] enable failed (${response.status})`, await safeText(response));
    throw new Error('Could not turn that schedule on.');
  }
  return (await response.json()) as JobDefinition;
}

/** Human-readable one-liner for a schedule (panel rows + the stop page). */
export function describeSchedule(def: JobDefinition): string {
  const zone = def.timeZone !== undefined ? ` (${def.timeZone})` : '';
  return def.schedule.type === 'one-time'
    ? `once at ${def.schedule.at}${zone}`
    : `recurring: ${def.schedule.cron}${zone}`;
}
